import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import Big from 'big.js';
import { Copy, Download } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { useIsMobile } from '@/shared/mobile';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/shared/ui/sheet';
import { toast } from '@/shared/ui/toaster';

import { columnsFor, type ColumnSpec } from './columns';
import { FILE_LINE_STATUS, type FileLine, type FileLinesKind } from './file-lines';
import { resolvePatternText } from './text';
import { ColumnValue, NotAvailable } from './value';
import { isBlank } from './values';

export interface FileLineDetailsSheetProps {
  readonly kind: FileLinesKind;
  /** A linha aberta; `undefined` mantém o painel vazio (fechando). */
  readonly line: FileLine | undefined;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** "Baixar boleto" (remessa) ou "Baixar recibo" (retorno): o fio e o aviso são do módulo. */
  readonly onDownload: (line: FileLine) => unknown;
}

interface DetailRow {
  readonly line: FileLine;
  readonly finePercent?: string | undefined;
  readonly fineAmount?: number | null | undefined;
}

const col = columnsFor<DetailRow>();

/** Itens do cartão, na ordem do legado (`ShowDetailsFiles.tsx:45-62`). */
const DETAIL_ITEMS: readonly ColumnSpec[] = [
  col.number('line.sequentialNumber', msg`Número sequencial`, { maxFractionDigits: 0 }),
  // O legado mostra o status da operação no item "Instrução" (`ShowDetailsFiles.tsx:48`); fiel até o Leon decidir.
  col.text('line.detail.statusOperationValueDisplay', msg`Instrução`),
  col.text('line.detail.barCode', msg`Código de barras`),
  col.text('line.detail.walletNumber', msg`Número carteira`),
  col.text('line.detail.payerName', msg`Pagador`),
  col.date('line.detail.createdAt', msg`Criado em`),
  col.date('line.detail.dueDate', msg`Data de vencimento`),
  col.text('line.detail.documentNumber', msg`Número do documento`),
  col.text('line.detail.ourNumberDigit', msg`Nosso número`),
  col.text('line.detail.participantControlNumber', msg`Controle do participante`),
  col.money('line.detail.amount', msg`Valor`),
  col.money('line.detail.discount', msg`Valor desconto`),
  col.money('line.detail.deduction', msg`Valor abatimento`),
  col.text('line.detail.fineTypeValueDisplay', msg`Multa`),
];

/** Multa em percentual vem em centésimos de ponto (`fineDecimal / 100`%); em valor, em centavos (`:61`). */
function detailRow(line: FileLine): DetailRow {
  const fine = line.detail?.fineDecimal;
  if (line.detail?.fineTypeValue === 'Percent') {
    return { line, finePercent: typeof fine === 'number' && Number.isFinite(fine) ? new Big(fine).div(10_000).toFixed() : undefined };
  }
  return { line, fineAmount: fine };
}

function BarCode({ value }: { readonly value: string }) {
  const { t } = useLingui();
  const copy = () => {
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success(t`Sucesso`, { description: t`Código de barras copiado com sucesso para a sua área de transferência.` }))
      .catch(() => toast.error(t`Ops, houve um erro`, { description: t`Não foi possível copiar o código de barras` }));
  };
  return (
    <span className="flex min-w-0 items-center gap-1">
      <span className="min-w-0 truncate font-mono" title={value}>{`${value.substring(0, 32)}...`}</span>
      <Button type="button" variant="ghost" size="icon" className="size-7 shrink-0 text-muted-foreground" aria-label={t`Copiar código de barras`} onClick={copy}>
        <Copy aria-hidden className="size-3.5" />
      </Button>
    </span>
  );
}

function Items({ items, compact }: { readonly items: readonly { readonly id: string; readonly label: ReactNode; readonly value: ReactNode }[]; readonly compact: boolean }) {
  return (
    <div className="@container/dl rounded-card border border-border bg-card p-4">
      <dl className={cn('grid grid-cols-1 gap-x-6', compact ? 'gap-y-3' : 'gap-y-4 @min-[360px]/dl:grid-cols-2')}>
        {items.map((item) => (
          <div key={item.id} className="flex min-w-0 flex-col gap-0.5">
            <dt className="text-xs text-muted-foreground">{item.label}</dt>
            <dd className="min-w-0 text-sm font-medium break-words text-foreground">{item.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * "Ver detalhes" de uma linha de remessa ou retorno (`FileLinesContainer.tsx:128-144` +
 * `ShowDetailsFiles.tsx`): status e erro (só remessa), os dados do boleto, "Fechar" e o download.
 * Web: painel à direita; iPhone: tela cheia, uma coluna, botões presos embaixo.
 */
export function FileLineDetailsSheet({ kind, line, open, onOpenChange, onDownload }: FileLineDetailsSheetProps) {
  const { i18n, t } = useLingui();
  const isMobile = useIsMobile();
  const [downloading, setDownloading] = useState(false);
  const detail = line?.detail;
  const remessa = kind === 'remessa';
  const row = line ? detailRow(line) : undefined;
  const value = (spec: ColumnSpec) => (row ? <ColumnValue column={spec} row={row} /> : <NotAvailable />);

  const statusItems = remessa
    ? [
        { id: 'status', label: t`Status`, value: value(col.status('line.detail.statusOperationValue', msg`Status`, FILE_LINE_STATUS, { fallbackField: 'line.detail.statusOperationValueDisplay' })) },
        // `detail?.error !== null`: sem `detail`, o legado também mostra "Erro: N/D" (`ShowDetailsFiles.tsx:33`).
        ...(detail?.error !== null ? [{ id: 'error', label: t`Erro`, value: detail?.error ? detail.error : <NotAvailable /> }] : []),
      ]
    : [];

  const fineSpec = row?.finePercent !== undefined ? col.percent('finePercent', msg`Valor da multa`) : col.money('fineAmount', msg`Valor da multa`);
  const items = DETAIL_ITEMS.map((spec) => ({
    id: spec.field,
    label: resolvePatternText(i18n, spec.label),
    value: spec.field === 'line.detail.barCode' && detail?.barCode ? <BarCode value={detail.barCode} /> : value(spec),
  }));
  items.push({ id: 'fine', label: t`Valor da multa`, value: value(fineSpec) });

  const blocked = !isBlank(detail?.error) || isBlank(detail?.bankSlipId) || isBlank(detail?.barCode);
  const download = async () => {
    if (!line || downloading) return;
    setDownloading(true);
    try {
      await onDownload(line);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-slot="file-line-details" size="md" className={cn(isMobile && 'max-w-none border-l-0')}>
        <SheetHeader className="pt-[calc(env(safe-area-inset-top,0px)+16px)]">
          <SheetTitle>{t`Ver detalhes`}</SheetTitle>
          <SheetDescription>{t`Confira as informações`}</SheetDescription>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-4">
          <h3 className="text-base font-semibold text-foreground">{t`Detalhes`}</h3>
          {remessa ? <Items items={statusItems} compact={isMobile} /> : null}
          <Items items={items} compact={isMobile} />
          <Alert tone="info" title={t`Informação sobre o boleto baixado`}>
            {t`Este documento representa a versão atual do boleto e não deve ser confundido com a instrução de pagamento original.`}
          </Alert>
        </SheetBody>
        <SheetFooter className={cn(isMobile && 'pb-[calc(env(safe-area-inset-bottom,0px)+12px)] [&>*]:flex-1')}>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t`Fechar`}
          </Button>
          <Button type="button" disabled={blocked || !line} loading={downloading} onClick={() => void download()}>
            <Download aria-hidden />
            {remessa ? t`Baixar boleto` : t`Baixar recibo`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
